// Plain-English vocabulary for the Failed Prints screen.
// The backend sends machine codes (reason_code, event, origin, docket_type); everything a
// human reads on screen is defined here so the wording can be tuned without touching the API.

export const SEVERITY = {
  critical: { label: "Not printed", className: "sevCritical" },
  warning: { label: "Delayed", className: "sevWarning" },
};

export const REASONS = {
  BUILD_CRASH: {
    title: "Docket could not be built",
    severity: "critical",
    summary:
      "The system crashed while preparing the docket, so nothing was sent to the printer.",
    action:
      "Reprinting will probably fail the same way. Copy the technical details to the dev team, and read the order from the Orders screen in the meantime.",
  },
  PRINTER_OFFLINE: {
    title: "Printer was offline",
    severity: "warning",
    summary:
      "The docket was built and queued, but the printer was disconnected when it was sent.",
    action:
      "Check the printer is switched on, has paper and is on the network. The docket stays queued for up to 12 hours and should print when the printer reconnects. If the order is urgent, reprint on another printer.",
  },
  NO_PRINTER: {
    title: "No printer set up for this docket",
    severity: "critical",
    summary:
      "There is no active printer assigned to receive this kind of docket, so it had nowhere to go.",
    action:
      "Open Hardware → Printers and make sure an active printer has this docket role switched on.",
  },
  QUEUE_WRITE_FAILED: {
    title: "Could not reach the print queue",
    severity: "critical",
    summary:
      "The docket was built but the system failed to hand it to the print queue (Redis), so the printer never received it.",
    action:
      "Reprint the order. If several of these appear together, tell the dev team: the print queue may be down.",
  },
  STALLED: {
    title: "Print started but never finished",
    severity: "critical",
    summary:
      "The print job began, then stopped with no error and no confirmation. This usually means it crashed part-way.",
    action:
      "Treat the docket as not printed and reprint it. Copy the technical details for the dev team.",
  },
  NO_MATCHING_STATION: {
    title: "No printer matched the order's items",
    severity: "critical",
    summary:
      "Printers exist for this docket, but every one was skipped because none is set up to print these items or categories.",
    action:
      "Open Hardware → Printers and check which categories or items each kitchen printer is linked to.",
  },
  NOT_STARTED: {
    title: "Print never started",
    severity: "critical",
    summary:
      "The print was requested, but the print job never started, so the docket was never prepared.",
    action:
      "Reprint the order. If several of these appear together, tell the dev team: the print job queue may be stuck.",
  },
  JOB_FAILED: {
    title: "Print job failed",
    severity: "critical",
    summary: "The print job stopped with an error before the docket was sent.",
    action: "Reprint the order. If it keeps failing, copy the technical details for the dev team.",
  },
};

export const ORIGINS = {
  pos: { label: "POS", className: "originPos" },
  website: { label: "Website", className: "originWeb" },
  uber: { label: "Uber Eats", className: "originUber" },
  doordash: { label: "DoorDash", className: "originDoordash" },
};

export const DOCKET_TYPES = {
  kitchen: { label: "Kitchen" },
  billing: { label: "Billing" },
};

// Which print event this was, in words a person would use.
export const EVENT_LABELS = {
  KITCHEN_UBER: "Uber Eats kitchen docket",
  BILLING_UBER: "Uber Eats billing docket",
  KITCHEN_WEB: "Website order kitchen docket",
  BILLING_WEB: "Website order billing docket",
  KITCHEN_POS_DINE: "Dine-in kitchen docket",
  BILLING_POS_DINE: "Dine-in billing docket",
  KITCHEN_POS_TAKEWAY: "Takeaway kitchen docket",
  BILLING_POS_TAKEWAY: "Takeaway billing docket",
  "send-to-kitchen": "Send to kitchen",
  "dine-in-finalize": "Dine-in bill (finalise table)",
  "phone-order": "Phone order docket",
  "website-order": "Website order docket",
  "on-demand-order": "On-demand order docket",
};

export const STEP_STATUS_LABELS = {
  ok: "Done",
  failed: "Failed",
  missing: "Never happened",
  skipped: "Skipped",
};

export const eventLabel = (event) => EVENT_LABELS[event] || event || "Unknown print";

export const reasonMeta = (code) => REASONS[code] || REASONS.JOB_FAILED;

export const isResolved = (f) => Boolean(f.resolved_at);

// Was the printer offline at the moment this docket was sent? (Falls back to its current state.)
export const printerWasOnline = (printer) =>
  printer.online_at_send !== undefined && printer.online_at_send !== null
    ? printer.online_at_send
    : printer.online;

export const formatClock = (iso) =>
  new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

export const formatDay = (iso) =>
  new Date(iso).toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });

export const timeAgo = (iso, now = Date.now()) => {
  const mins = Math.max(0, Math.round((now - new Date(iso).getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ${mins % 60} min ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
};

export const durationText = (fromIso, now = Date.now()) => {
  const mins = Math.max(0, Math.round((now - new Date(fromIso).getTime()) / 60000));
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  return `${hrs} hr ${mins % 60} min`;
};

// Plain text block staff can paste to the dev team in one tap.
export const buildSupportText = (f) => {
  const meta = reasonMeta(f.reason_code);
  const lines = [
    `FAILED PRINT — ${meta.title}`,
    `Time: ${new Date(f.occurred_at).toLocaleString()}`,
    `Order: #${f.order_id}${f.parent_order_id ? ` (split from #${f.parent_order_id})` : ""}`,
    `Source: ${ORIGINS[f.origin]?.label || f.origin} — ${f.origin_detail || ""}`,
    `Docket: ${DOCKET_TYPES[f.docket_type]?.label || f.docket_type} (${eventLabel(f.event)})`,
    `Printer: ${f.printer ? `${f.printer.name} (id ${f.printer.id}, format ${f.printer.docket_version}, ${printerWasOnline(f.printer) === false ? "OFFLINE when sent" : "online"})` : "none"}`,
    `Why: ${f.detail}`,
  ];
  if (f.resolved_at) lines.push(`Later reprint succeeded at ${new Date(f.resolved_at).toLocaleTimeString()}`);
  if (f.technical?.message) lines.push(`Error: ${f.technical.message}`);
  if (f.technical?.file) lines.push(`Where: ${f.technical.file}:${f.technical.line}`);
  lines.push(`Restaurant: ${f.restaurant_id}`);
  return lines.join("\n");
};
